import React, { useEffect, useState, useRef, useContext } from "react";
import { Container, Button, Row, Col, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import CommonForm from "../../../../Components/Common/CommonForm";
import SectionCard from "../../../../Components/Common/SectionCard";
import FilePreviewModal from "../../../../Components/Common/FilePreviewModal";
import FullPageLoader from "../../../../Components/Common/FullPageLoader";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../css/addBlockVolunteer.module.scss";
import addVolunteerStyle from "../../MasterVolunteer/css/volunteer.module.scss";
import {
  getSubjectFields,
  getBlockTypeFields,
  getDateFields,
  reasonFields,
  eligibilityFields,
} from "../helper/blockvolunteer.constant";
import { BlockVolunteerFormValues } from "../helper/blockvolunteer.types";
import { BLOCK_VOLUNTEER_PATHS, SUBJECT_PATHS } from "Routes/paths";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { BLOCK_REASONS_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import { showToastMessage, GetUserData, formatDateSplit } from "common/service/CommonService";
import { useBlockVolunteerService } from "../api/useBlockVolunteerQuery";
import Reason from "common/utilities/Reason/Reason";
import { queryClient } from "api/queryClient";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import CommonFileProgress from "../../../../Components/Common/CommonFileUpload/CommonFileProgress";

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};

const AddBlockVolunteer: React.FC = () => {
  document.title = "Block Volunteer | ZENTIXS VISTA";

  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();

  // Local Storage Data
  const selectedsubject = sessionStorage.getItem("subjectno") || "";
  const selectedvolunteerid = Number(sessionStorage.getItem("activevolunteerid"));
  const selectedstudycode = sessionStorage.getItem("studycode");
  const selectedstudyid = Number(sessionStorage.getItem("activestudyid"));

  // -------------------------------------------------------------------------------------------
  // 2. Form Setup
  // -------------------------------------------------------------------------------------------
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<BlockVolunteerFormValues>({
    defaultValues: {
      studycode: selectedstudycode,
      subjectno: selectedsubject,
      subjectinitial: "",
      blocktype: "Temporary",
      startdate: "",
      enddate: "",
      blockduration: "0 days",
      blockreason: "",
      remarks: "",
      rescreeningeligibility: "",
    },
    mode: "onBlur",
  });

  const blockType = watch("blocktype");
  const startDate = watch("startdate");
  const endDate = watch("enddate");

  // -------------------------------------------------------------------------------------------
  // 3. Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectQuery, InsertMutation, UpdateMutation } = useBlockVolunteerService({
    blockid: editId ? Number(editId) : null,
  });
  const blockRecord = SelectQuery?.data?.data?.[0];

  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: editId ? blockRecord?.volunteerid : selectedvolunteerid,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data[0];
  const profileurl = sessionStorage.getItem("filebaseUrl") + "/" + volunteerDetails?.profileimg;

  // -------------------------------------------------------------------------------------------
  // 4. States
  // -------------------------------------------------------------------------------------------
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState<any | null>(null);

  // File Upload & Preview States
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const breadcrumbContext = useContext(BreadcrumbContext);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB limit

  // -------------------------------------------------------------------------------------------
  // 5. Effects
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = `${editId ? "Edit" : "Add"} Block Record | ZENTIXS ORBIT`;
  }, [editId]);

  useEffect(() => {
    editId ? (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Block Volunteer", link: BLOCK_VOLUNTEER_PATHS.LIST },
      { text: "Edit Block Record" },
    ])) : (breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Subject Detail", link: SUBJECT_PATHS.INFO },
      { text: "Add Block Record" },
    ]));

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId(["BLOCK_REASONS_OPTIONS"]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (volunteerDetails?.initials) {
      setValue("subjectinitial", volunteerDetails.initials);
    }
  }, [volunteerDetails, setValue]);

  useEffect(() => {
    if (editId && blockRecord) {
      setExistingAttachments(blockRecord.attachments || []);
      reset({
        studycode: blockRecord.studycode,
        subjectno: blockRecord.subjectno,
        subjectinitial: blockRecord.initials,
        blocktype: blockRecord.blocktype as any,
        startdate: blockRecord.startdate || "",
        enddate: blockRecord.enddate || "",
        blockduration: blockRecord.blockduration,
        blockreason: blockRecord.blockreason,
        remarks: blockRecord.remarks,
        rescreeningeligibility: blockRecord.rescreeningeligibility,
      });
    } else {
      reset({
        studycode: selectedstudycode || "",
        subjectno: selectedsubject || "",
        subjectinitial: volunteerDetails?.initials || "",
        blocktype: "Temporary",
        startdate: "",
        enddate: "",
        blockduration: "0 days",
        blockreason: "",
        remarks: "",
        rescreeningeligibility: "",
      });
    }
  }, [editId, blockRecord, reset, selectedstudycode, selectedsubject, volunteerDetails]);

  // Update duration based on dates
  useEffect(() => {
    if (blockType === "Permanent") {
      setValue("blockduration", "0");
      return;
    }

    if (!startDate) {
      setValue("enddate", "");
      setValue("blockduration", "0 days");
      return;
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        setValue("enddate", "");
        setValue("blockduration", "0 days");
        return;
      }
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setValue("blockduration", diffDays > 0 ? `${diffDays} days` : "0 days");
    } else {
      setValue("blockduration", "0 days");
    }
  }, [startDate, endDate, blockType, setValue]);

  // -------------------------------------------------------------------------------------------
  // 6. Handlers
  // -------------------------------------------------------------------------------------------
  const btnReasonClick = (reasonPayload: { reason: string }) => {
    if (!pendingUpdatePayload || !editId) return;

    UpdateMutation.mutate(
      {
        ...pendingUpdatePayload,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries({ queryKey: ["block-volunteer-select-all"] });
          showToastMessage("success", res?.message || "Block record updated successfully");
          setIsReasonModal(false);
          setPendingUpdatePayload(null);
          navigate(BLOCK_VOLUNTEER_PATHS.LIST);
        },
        onError: (err: any) => {
          console.error("Update Error:", err);
          showToastMessage("error", err?.message || "Update failed");
        },
      }
    );
  };

  const onSubmit = (values: BlockVolunteerFormValues) => {
    const { studycode, ...restValues } = values;
    const payload = {
      ...restValues,
      blocksource: "post-enrollment",
      volunteerid: editId && blockRecord ? Number(blockRecord.volunteerid) : selectedvolunteerid,
      studyid: editId && blockRecord ? Number(blockRecord.studyid) : selectedstudyid,
      subjectno: editId && blockRecord ? blockRecord.subjectno : selectedsubject,
      attachments: uploadedFiles,
    };
    if (editId) {
      const updatePayload = {
        ...payload,
        blockid: Number(editId),
        deletedAttachmentIds: deletedAttachmentIds,
      };
      setPendingUpdatePayload(updatePayload);
      setIsReasonModal(true);
    } else {
      InsertMutation.mutate(payload as any, {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries({ queryKey: ["block-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", response?.message || "Block Record Submitted successfully!");
          navigate(SUBJECT_PATHS.INFO);
        },
        onError: (err: any) => {
          console.log("Error", err)
          showToastMessage("error", err || "Failed to submit Block Record");
        },
      });
    }
  };

  const handleClear = () => {
    reset();
    setUploadedFiles([]);
  };

  // --- File Handlers ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      e.target.value = "";
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const currentTotal = uploadedFiles.reduce((sum, f) => sum + f.size, 0);
    const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      showToastMessage("error","Total attachment size cannot exceed 20 MB.");
      return;
    }
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleDeleteExisting = (attachmentId: number) => {
    setDeletedAttachmentIds((prev) => [...prev, attachmentId]);
    setExistingAttachments((prev) =>
      prev.filter((a) => Number(a.volunteerblockattachmentid) !== Number(attachmentId))
    );
  };

  const handlePreview = (file: any) => {
 
 const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    //NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    //EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  // -------------------------------------------------------------------------------------------
  // 7. Render
  // -------------------------------------------------------------------------------------------
  if (SelectQuery?.isFetching || VolunteerSelectQuery?.isFetching) {
    return <FullPageLoader message="Fetching details..." />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h5 className="mb-0 fw-bold text-dark">{editId ? "Edit Block Record" : "Add Block Record"}</h5>
            </div>
            <p className="text-muted fs-13 mb-0">
              Restrict volunteer participation in clinical studies due to protocol violations, safety concerns, or administrative exclusions.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* SUBJECT IDENTIFICATION */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Identification"
            subtitle="Select the volunteer to be blocked from study participation"
          >
            <CommonForm
              fields={getSubjectFields()}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
            />

            {volunteerDetails && (
              <div className={styles.subjectInfoStrip}>
                <img src={profileurl} alt={volunteerDetails.initials} className={styles.subjectAvatar} />
                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Age / Gender</span>
                    <span className={styles.metaValue}>
                      {volunteerDetails.age} / {volunteerDetails.gendertext}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Blood Group</span>
                    <span className={styles.metaValue}>{volunteerDetails.bloodgrouptext || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Weight</span>
                    <span className={styles.metaValue}>{volunteerDetails.weight || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Height</span>
                    <span className={styles.metaValue}>{volunteerDetails.height || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>BMI</span>
                    <span className={styles.metaValue}>{volunteerDetails.bmi || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Prior Blocks</span>
                    <span className={styles.metaValue}>{volunteerDetails.priorBlocks || "—"}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Current Status</span>
                    <span className={`${styles.metaValue} ${styles.statusActive}`}>{volunteerDetails.status}</span>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          {/* BLOCK CLASSIFICATION */}
          <SectionCard
            icon="ri-focus-2-line"
            title="Block Classification"
            subtitle="Specify the type, duration, and reason for this block"
          >
            <div className="mb-2">
              <CommonForm
                fields={getBlockTypeFields(blockType, !!editId)}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />
            </div>

            {blockType === "Temporary" ? (
              <>
                <div className="mb-2">
                  <CommonForm
                    fields={getDateFields(blockType, startDate)}
                    control={control}
                    errors={errors}
                    columns={3}
                    setValue={setValue}
                  />
                </div>
                <div className="mb-2">
                  <Row className="align-items-start">
                    <Col md={4}>
                      <CommonForm
                        fields={[reasonFields(BLOCK_REASONS_OPTIONS)[0]]}
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </Col>
                    <Col md={8}>
                      <CommonForm
                        fields={[reasonFields(BLOCK_REASONS_OPTIONS)[1]]}
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </Col>
                  </Row>
                </div>
              </>
            ) : (
              <div className="mb-2">
                <Row className="align-items-start">
                  <Col md={4}>
                    <CommonForm
                      fields={getDateFields(blockType, startDate)}
                      control={control}
                      errors={errors}
                      columns={1}
                      setValue={setValue}
                    />
                  </Col>
                  <Col md={4}>
                    <CommonForm
                      fields={[reasonFields(BLOCK_REASONS_OPTIONS)[0]]}
                      control={control}
                      errors={errors}
                      columns={1}
                      setValue={setValue}
                    />
                  </Col>
                  <Col md={4}>
                    <CommonForm
                      fields={[reasonFields(BLOCK_REASONS_OPTIONS)[1]]}
                      control={control}
                      errors={errors}
                      columns={1}
                      setValue={setValue}
                    />
                  </Col>
                </Row>
              </div>
            )}

            {blockType === "Temporary" && (
              <div>
                <CommonForm
                  fields={eligibilityFields}
                  control={control}
                  errors={errors}
                  columns={1}
                  setValue={setValue}
                />
              </div>
            )}
          </SectionCard>

          {/* ATTACHMENTS & VERIFICATION */}
          <SectionCard
            icon="ri-attachment-2"
            title="Attachments & Verification"
            subtitle="Upload supporting documents and confirm receipt details."
          >
            {/* File Upload Zone */}
            <div className="mb-4">
              <label className="form-label text-dark small fw-medium">
                Upload Documents (Protocol Deviation, Medical Report, IRB Approval, etc.)
              </label>

              <CommonFileProgress
                files={uploadedFiles}
                existingSizeBytes={existingAttachments.reduce((sum, f) => sum + parseSizeToBytes(f.size), 0)}
                maxSizeMB={20}
              />

              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `1px dashed ${isDragging ? "#2E86AB" : "#d0d5dd"}`,
                  borderRadius: 8,
                  padding: "40px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: isDragging ? "#f0f7ff" : "#fafbfc",
                  transition: "all 0.2s",
                }}
              >
                <i
                  className="ri-upload-cloud-2-line text-primary"
                  style={{ fontSize: 32, display: "block", marginBottom: 8 }}
                />
                <p className="mb-1 fw-bold text-dark" style={{ fontSize: 14 }}>
                  Click to upload or drag and drop
                </p>
                <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                  Supports PDF, JPG, PNG (Max 20MB)
                </p>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  className="border px-3"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Existing attachments from server */}
              {existingAttachments.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">Existing Attachments</p>

                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {existingAttachments.map((file, idx) => (
                      <Col
                        key={`existing-${idx}`}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>

                          {/* FILE INFO */}
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.filename} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {file.size}
                              </div>
                            </div>
                          </div>

                          {/* ACTIONS */}
                          <div className={addVolunteerStyle.fileActions}>
                            {/* PREVIEW */}
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              style={{ cursor: "pointer", marginRight: "10px" }}
                              onClick={() => handlePreview(file)}
                            />

                            {/* DELETE */}
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDeleteExisting(file.volunteerblockattachmentid)}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {/* Newly uploaded files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  <p className="text-muted small fw-medium mb-1">New Attachments</p>
                  <Row className="gap-2 px-0 w-100 mx-auto">
                    {uploadedFiles.map((file, idx) => (
                      <Col
                        key={idx}
                        md={4}
                        sm={12}
                        className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                      >
                        <div className={`${addVolunteerStyle.attachmentCard} h-100`}>
                          <div className={addVolunteerStyle.fileInfo}>
                            <div className={addVolunteerStyle.fileIcon}>
                              <i className="ri-file-pdf-line text-danger" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={addVolunteerStyle.fileName}>
                                <TruncatedText text={file.name} />
                              </div>
                              <div className={addVolunteerStyle.fileMeta}>
                                {(file.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <div className={addVolunteerStyle.fileActions}>
                            <i
                              className="ri-eye-line text-primary"
                              title="Preview"
                              onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                              style={{ cursor: "pointer", marginRight: "10px" }}
                            />
                            <i
                              className="ri-delete-bin-line text-danger"
                              title="Delete"
                              onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button type="button" color="dark" outline onClick={() => {
                handleClear();
                if (editId) {
                  navigate(BLOCK_VOLUNTEER_PATHS.LIST);
                } else {
                  navigate(SUBJECT_PATHS.INFO_SUBJECT_ACTION);
                }
              }}>
                Cancel
              </Button>
              <div className="d-flex gap-2">
                {/* <Button type="button" color="secondary" outline onClick={() => alert("Draft Saved!")}>
                      <i className="ri-save-line me-1" /> Save as Draft
                    </Button> */}
                <Button type="submit" color="primary" disabled={InsertMutation.isPending}>
                  {InsertMutation.isPending ? <Spinner size="sm" className="me-2" /> : <i className="ri-lock-line me-1" />}
                  {editId ? "Update Block Record" : "Submit Block Record"}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* File Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={() => setIsReasonModal(!isReasonModal)}
            action="Update"
            id={Number(editId)}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddBlockVolunteer;
